import React from "react";
import PropTypes from "prop-types";
import {
  CalendarDays,
  Edit2,
  XCircle,
  FileText,
} from "lucide-react";
import {
  formatDateTime,
  capitalize,
} from "../../../utilities/functions/Helpers";
import FacilityConstants from "../../../utilities/constants/FacilityConstants";

const AppointmentCard = ({
  appointment,
  onEdit,
  onCancel,
  isHighlighted = false,
  highlightLabel = null,
}) => {
  const { dateTime, appointmentType, status, description } = appointment;

  const isInThePast = new Date(dateTime) < new Date();
  const isCancelled = status === FacilityConstants.STATUS.CANCELLED;
  const isDone = status === FacilityConstants.STATUS.DONE;
  const isEditable = !isInThePast && !isDone && !isCancelled;
  const isCancelable = !isInThePast && !isDone && !isCancelled;

  const getStatusColor = (status) => {
    switch (status) {
      case FacilityConstants.STATUS.PENDING:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
      case FacilityConstants.STATUS.APPROVED:
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case FacilityConstants.STATUS.CANCELLED:
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case FacilityConstants.STATUS.DONE:
        return "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-400";
      default:
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    }
  };

  return (
    <div
      data-appointment-id={appointment.id}
      className={`bg-white dark:bg-slate-900 rounded-xl border shadow-sm hover:shadow-md overflow-hidden flex flex-col h-full transition-all duration-500 ${
        isHighlighted
          ? "border-emerald-400 dark:border-emerald-500 ring-2 ring-emerald-200 dark:ring-emerald-500/30 shadow-emerald-100 dark:shadow-emerald-950/20 -translate-y-1"
          : "border-slate-200 dark:border-slate-800"
      }`}
    >
      {/* Card Header */}
      <div className="h-28 bg-gradient-to-r from-purple-500 to-pink-600 relative p-4 flex flex-col justify-between">
        <div className="flex justify-between items-start">
          <span
            className={`px-2 py-1 rounded-md text-xs font-bold uppercase ${getStatusColor(status)} shadow-sm`}
          >
            {status}
          </span>
          {isHighlighted && (
            <span className="rounded-full bg-white/20 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
              {highlightLabel === "created" ? "Just created" : "Just updated"}
            </span>
          )}
        </div>
        <h3 className="text-white text-xl font-bold font-serif tracking-wide shadow-black/20 drop-shadow-md truncate">
          {capitalize(appointmentType || "Appointment")}
        </h3>
      </div>

      {/* Card Body */}
      <div className="p-5 flex-1 space-y-4">
        <div className="space-y-3">
          <div className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
            <CalendarDays
              size={18}
              className="mt-0.5 shrink-0 text-purple-500"
            />
            <div>
              <p className="text-sm font-semibold">Scheduled Date</p>
              <p className="text-sm">{formatDateTime(dateTime)}</p>
            </div>
          </div>

          {description && (
            <div className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
              <FileText size={18} className="mt-0.5 shrink-0 text-blue-500" />
              <div className="min-w-0">
                <p className="text-sm font-semibold">Reason / Details</p>
                <p className="text-sm truncate" title={description}>
                  {description.length > 50
                    ? `${description.substring(0, 50)}...`
                    : description}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card Footer / Actions */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex gap-2">
        {isEditable ? (
          <button
            onClick={() => onEdit(appointment)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Edit2 size={16} /> Edit
          </button>
        ) : (
          <button
            disabled
            className="flex-1 px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-slate-400 cursor-not-allowed text-center"
          >
            {isDone ? "Completed" : "View Only"}
          </button>
        )}

        {isCancelable && (
          <button
            onClick={() => onCancel(appointment.id)}
            className="flex items-center justify-center px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            title="Cancel Appointment"
          >
            <XCircle size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

AppointmentCard.propTypes = {
  appointment: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isHighlighted: PropTypes.bool,
  highlightLabel: PropTypes.oneOf(["created", "updated", null]),
};

export default AppointmentCard;
